/**
 * useSecureForms Hook
 * Custom hook for managing secure forms in React components
 */

import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import secureFormService from '../services/secureFormService';
import { apiRequest } from '../config/authConfig';

/**
 * Hook for managing all secure forms
 * @returns {Object} Forms state and management functions
 */
export const useSecureForms = () => {
    const { instance, accounts } = useMsal();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getAccessToken = useCallback(async () => {
        if (!accounts || accounts.length === 0) {
            throw new Error('No active account. Please sign in.');
        }

        try {
            const response = await instance.acquireTokenSilent({
                ...apiRequest,
                account: accounts[0]
            });
            return response.accessToken;
        } catch (error) {
            console.error('Failed to acquire token:', error);
            throw error;
        }
    }, [instance, accounts]);

    const loadForms = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await getAccessToken();
            const data = await secureFormService.getAllForms(token);
            setForms(data);
        } catch (err) {
            console.error('Failed to load forms:', err);
            setError(err.message || 'Failed to load forms');
        } finally {
            setLoading(false);
        }
    }, [getAccessToken]);

    const createForm = useCallback(async (formData) => {
        try {
            const token = await getAccessToken();
            const newForm = await secureFormService.createForm(token, formData);
            setForms(prevForms => [...prevForms, newForm]);
            return newForm;
        } catch (err) {
            console.error('Failed to create form:', err);
            throw err;
        }
    }, [getAccessToken]);

    const updateForm = useCallback(async (formId, updateData) => {
        try {
            const token = await getAccessToken();
            const updatedForm = await secureFormService.updateForm(token, formId, updateData);
            setForms(prevForms => 
                prevForms.map(form => 
                    form.formId === formId ? updatedForm : form
                )
            );
            return updatedForm;
        } catch (err) {
            console.error('Failed to update form:', err);
            throw err;
        }
    }, [getAccessToken]);

    const deactivateForm = useCallback(async (formId) => {
        try {
            const token = await getAccessToken();
            await secureFormService.deactivateForm(token, formId);
            setForms(prevForms => prevForms.filter(form => form.formId !== formId));
        } catch (err) {
            console.error('Failed to deactivate form:', err);
            throw err;
        }
    }, [getAccessToken]);

    const getForm = useCallback(async (formId) => {
        try {
            const token = await getAccessToken();
            return await secureFormService.getForm(token, formId);
        } catch (err) {
            console.error('Failed to get form:', err);
            throw err;
        }
    }, [getAccessToken]);

    useEffect(() => {
        if (accounts && accounts.length > 0) {
            loadForms();
        }
    }, [accounts, loadForms]);

    return {
        forms,
        loading,
        error,
        reload: loadForms,
        createForm,
        updateForm,
        deactivateForm,
        getForm
    };
};

/**
 * Hook for managing forms by purpose
 * @param {string} purpose - Form purpose filter
 * @returns {Object} Filtered forms state
 */
export const useFormsByPurpose = (purpose) => {
    const { instance, accounts } = useMsal();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getAccessToken = useCallback(async () => {
        if (!accounts || accounts.length === 0) {
            throw new Error('No active account. Please sign in.');
        }

        try {
            const response = await instance.acquireTokenSilent({
                ...apiRequest,
                account: accounts[0]
            });
            return response.accessToken;
        } catch (error) {
            console.error('Failed to acquire token:', error);
            throw error;
        }
    }, [instance, accounts]);

    useEffect(() => {
        const loadForms = async () => {
            if (!purpose) {
                setForms([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const token = await getAccessToken();
                const data = await secureFormService.getFormsForPurpose(token, purpose);
                setForms(data);
            } catch (err) {
                console.error('Failed to load forms for purpose:', err);
                setError(err.message || 'Failed to load forms');
            } finally {
                setLoading(false);
            }
        };

        if (accounts && accounts.length > 0 && purpose) {
            loadForms();
        }
    }, [purpose, accounts, getAccessToken]);

    return { forms, loading, error };
};

/**
 * Hook for getting default form for a purpose
 * @param {string} purpose - Form purpose
 * @returns {Object} Default form state
 */
export const useDefaultForm = (purpose) => {
    const { instance, accounts } = useMsal();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getAccessToken = useCallback(async () => {
        if (!accounts || accounts.length === 0) {
            throw new Error('No active account. Please sign in.');
        }

        try {
            const response = await instance.acquireTokenSilent({
                ...apiRequest,
                account: accounts[0]
            });
            return response.accessToken;
        } catch (error) {
            console.error('Failed to acquire token:', error);
            throw error;
        }
    }, [instance, accounts]);

    useEffect(() => {
        const loadDefaultForm = async () => {
            if (!purpose) {
                setForm(null);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const token = await getAccessToken();
                const data = await secureFormService.getDefaultForm(token, purpose);
                setForm(data);
            } catch (err) {
                console.error('Failed to load default form:', err);
                setError(err.message || 'Failed to load default form');
            } finally {
                setLoading(false);
            }
        };

        if (accounts && accounts.length > 0 && purpose) {
            loadDefaultForm();
        }
    }, [purpose, accounts, getAccessToken]);

    return { form, loading, error };
};

export default useSecureForms;
